import React, { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon } from 'lucide-react'
import { templateAPI } from '../../services/api'
import EventCreationForm from '../Calendar/EventCreationForm'
import EventDetailsModal from '../Calendar/EventDetailsModal'

const DashboardCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [calendarData, setCalendarData] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false)
  const [templates, setTemplates] = useState([])

  useEffect(() => {
    fetchCalendarData()
    fetchTemplates()
  }, [currentDate])

  const fetchCalendarData = async () => {
    try {
      setLoading(true)
      
      // Calculate date range (45 days back, 45 days forward)
      const startDate = new Date(currentDate)
      startDate.setDate(startDate.getDate() - 45)
      
      const endDate = new Date(currentDate)
      endDate.setDate(endDate.getDate() + 45)
      
      const response = await templateAPI.getCombinedCalendarData({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      })
      
      if (response.data.success) {
        setCalendarData(response.data.data)
      }
    } catch (err) {
      console.error('Error fetching calendar data:', err)
      setError('Failed to load calendar data')
    } finally {
      setLoading(false)
    }
  }

  const fetchTemplates = async () => {
    try {
      const response = await templateAPI.getAll({ limit: 100 })
      if (response.data.success) {
        setTemplates(response.data.data.templates || [])
      }
    } catch (err) {
      console.error('Error fetching templates:', err)
    }
  }

  const handleEventCreated = (newEvent) => {
    // Refresh calendar data to show the new event
    fetchCalendarData()
    setShowEventModal(false)
    setSelectedDate(null)
  }

  const handleEventUpdated = (updatedEvent) => {
    fetchCalendarData()
    setShowEventDetailsModal(false)
    setSelectedEvent(null)
  }

  const handleEventDeleted = (eventId) => {
    fetchCalendarData()
    setShowEventDetailsModal(false)
    setSelectedEvent(null)
  }

  const handleEventClick = (event, e) => {
    e.stopPropagation()
    setSelectedEvent(event)
    setShowEventDetailsModal(true)
  }

  const getEventTypeIcon = (eventType) => {
    const icons = {
      'design_deadline': '🎨',
      'review': '👀',
      'meeting': '🤝',
      'reminder': '⏰',
      'client_feedback': '💬',
      'approval': '✅',
      'delivery': '📦',
      'review_deadline': '👀',
      'client_meeting': '🤝',
      'project_launch': '🚀',
      'content_creation': '📝',
      'social_media_post': '📱',
      'campaign_start': '📢',
      'campaign_end': '🏁',
      'other': '📅'
    }
    return icons[eventType] || '📅'
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(newDate.getMonth() + direction)
    setCurrentDate(newDate)
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0]
  }

  const getDataForDate = (date) => {
    const dateKey = formatDateKey(date)
    const dayData = calendarData[dateKey]
    if (!dayData) {
      return {
        designs: {
          total: 0,
          visible: 0,
          pending: 0,
          in_progress: 0,
          completed: 0,
          public: 0
        },
        events: []
      }
    }
    return {
      designs: dayData.designs || {
        total: 0,
        visible: 0,
        pending: 0,
        in_progress: 0,
        completed: 0,
        public: 0
      },
      events: dayData.events || []
    }
  }

  const isToday = (date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const isPastDate = (date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  const handleDateClick = (date) => {
    setSelectedDate(date)
    setShowEventModal(true)
  }

  const renderCalendarDay = (date, index) => {
    if (!date) {
      return <div key={index} className="h-32 border border-gray-200"></div>
    }

    const dayData = getDataForDate(date)
    const { designs, events } = dayData
    const isCurrentDay = isToday(date)
    const isPast = isPastDate(date)

    return (
      <div
        key={index}
        className={`h-32 border border-gray-200 p-1 cursor-pointer hover:bg-gray-50 ${
          isCurrentDay ? 'bg-blue-50 border-blue-300' : ''
        } ${isPast ? 'bg-gray-50' : ''}`}
        onClick={() => handleDateClick(date)}
      >
        <div className="flex flex-col h-full">
          {/* Date number */}
          <div className={`text-xs font-medium mb-1 ${
            isCurrentDay ? 'text-blue-600' : isPast ? 'text-gray-400' : 'text-gray-900'
          }`}>
            {date.getDate()}
          </div>
          
          {/* Events as badges */}
          <div className="flex-1 flex flex-col space-y-0.5 mb-1">
            {events.slice(0, 2).map((event, eventIndex) => (
              <div
                key={eventIndex}
                onClick={(e) => handleEventClick(event, e)}
                className="text-xs px-1 py-0.5 rounded truncate cursor-pointer"
                style={{
                  backgroundColor: `${event.color || '#3B82F6'}20`,
                  borderLeft: `2px solid ${event.color || '#3B82F6'}`,
                  color: event.color || '#3B82F6'
                }}
                title={event.title}
              >
                <span className="mr-0.5">{getEventTypeIcon(event.event_type)}</span>
                <span className="truncate">{event.title}</span>
              </div>
            ))}
            {events.length > 2 && (
              <div className="text-xs text-gray-500 px-1">
                +{events.length - 2} more
              </div>
            )}
          </div>
          
          {/* Design counts */}
          <div className="flex flex-col space-y-0.5">
            {designs.total > 0 && (
              <>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">{designs.total}</span> total
                  {designs.visible > 0 && (
                    <span className="ml-1">
                      (<span className="font-medium">{designs.visible}</span> visible)
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-0.5">
                  {designs.pending > 0 && (
                    <div className="text-xs text-yellow-600 bg-yellow-100 px-1 rounded">
                      {designs.pending} Pending
                    </div>
                  )}
                  {designs.in_progress > 0 && (
                    <div className="text-xs text-blue-600 bg-blue-100 px-1 rounded">
                      {designs.in_progress} In Progress
                    </div>
                  )}
                  {designs.completed > 0 && (
                    <div className="text-xs text-green-600 bg-green-100 px-1 rounded">
                      {designs.completed} Completed
                    </div>
                  )}
                  {designs.public > 0 && (
                    <div className="text-xs text-purple-600 bg-purple-100 px-1 rounded">
                      {designs.public} Public
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="text-center">
          <div className="text-red-500 mb-2">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchCalendarData}
            className="text-blue-600 hover:text-blue-700"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm">
      {/* Calendar Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <CalendarIcon className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => setShowEventModal(true)}
              className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create Event</span>
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-6">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {getDaysInMonth(currentDate).map((date, index) => 
            renderCalendarDay(date, index)
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="p-6 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-6 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-yellow-100 rounded"></div>
            <span className="text-yellow-600">P = Pending</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-blue-100 rounded"></div>
            <span className="text-blue-600">I = In Progress</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-green-100 rounded"></div>
            <span className="text-green-600">C = Completed</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-purple-100 rounded"></div>
            <span className="text-purple-600">Pub = Public</span>
          </div>
        </div>
      </div>

      {/* Event Creation Modal */}
      <EventCreationForm
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false)
          setSelectedDate(null)
        }}
        selectedDate={selectedDate}
        onEventCreated={handleEventCreated}
        templates={templates}
      />

      {/* Event Details Modal */}
      <EventDetailsModal
        isOpen={showEventDetailsModal}
        onClose={() => {
          setShowEventDetailsModal(false)
          setSelectedEvent(null)
        }}
        event={selectedEvent}
        onEventUpdated={handleEventUpdated}
        onEventDeleted={handleEventDeleted}
        templates={templates}
      />
    </div>
  )
}

export default DashboardCalendar
